import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, ExitScreen, TagChipInput } from './';
import { Warning } from '../assets/icons';
import { colour } from '../styles/GlobalStyles';
import { cloudFunctionV2, cloudFunctionV2Auth } from '../functions';
import { CRM_PROVIDERS, formatCreateResult, formatCreateSummary, formatCreateIssues, isLikelyTimeout, TIMEOUT_SUMMARY, TIMEOUT_MESSAGE } from '../utils/crmProviderConfigs';
import { getTimezoneAbbr } from '../utils/stateTimezones';
import { chunkBuyerIds, mergeChunkData, shouldContinueAfterChunk, isCleanFullRun } from '../utils/crmSendChunks';

// Renders the selector(s) + tags + warning + submit + result for whichever CRM
// is connected. Replaces three near-identical inline blocks (GHL / FUB / SF)
// from the old LeadBuyerMatches.js — config-driven via `CRM_PROVIDERS`.
//
// Props:
//   provider           — 'ghl' | 'fub' | 'salesforce'
//   action             — 'opportunity' | 'tag'
//   selectedBuyerIds   — Set<string>
//   context            — { companyId, propertyId, lead, calculatorInputs,
//                          isPortfolio, portfolio, portfolioLeads, imageStatus,
//                          selectedBuyerIds }
//   tags               — string[]
//   onTagsChange(next) — emit
//   onCompleteSent(msg)— called once after any successful create/tag with the
//                        result-count string so the parent can flip
//                        leadReviewStatus + show a terminal success state
//   onPipelinesEmpty() — called once per fetch cycle when the selector fetch
//                        resolves with zero pipelines/stages. Parent typically
//                        flips the segmented control to "Tag in CRM".

// The connected GHL sub-account interprets clozers_send_at in ITS OWN timezone,
// not the operator's — and that timezone differs per account. We fetch the
// account's location.timezone (surfaced by getGHLOpportunities) and reason
// entirely in it: the picker defaults to "now" in that zone, the displayed/
// edited wall-clock IS that zone, and the label says so — so an operator in any
// timezone schedules against the CRM's clock. Falls back to the operator's
// local timezone only if the account timezone can't be resolved.
const browserTimezone = () => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
    catch { return 'UTC'; }
};

// isLikelyTimeout / TIMEOUT_* now live in crmProviderConfigs so the self-purchase
// modal reasons about a maybe-succeeded send identically. On a timeout we
// deliberately do NOT mark the lead sent (onCompleteSent stays un-called) because
// we can't confirm success.

// "Now" in the given IANA timezone as a datetime-local value ("YYYY-MM-DDTHH:mm").
// A datetime-local input is naive (no tz), so seeding it with the target zone's
// wall-clock makes the operator edit in that zone with no browser conversion.
// Defaulting to a valid value also avoids leaving clozers_send_at blank, which
// can stall the workflow's wait-until-event-start step.
function nowInTimezone(iana) {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: iana, year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hour12: false,
        }).formatToParts(new Date());
        const g = (t) => parts.find((p) => p.type === t)?.value;
        const hour = g('hour') === '24' ? '00' : g('hour');
        return `${g('year')}-${g('month')}-${g('day')}T${hour}:${g('minute')}`;
    } catch {
        // Unknown/invalid IANA zone — fall back to the operator's local wall-clock
        // so the picker still gets a valid, non-blank default.
        const d = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
}

function CrmActionForm({ provider, action, selectedBuyerIds, context, tags, onTagsChange, onCompleteSent, onPipelinesEmpty }) {
    const config = CRM_PROVIDERS[provider];

    // Selector state lives here so it survives provider tab toggles within a session
    // but resets if the provider itself changes (rare — usually requires a settings flip).
    const [pipelines, setPipelines] = useState([]); // [{ id, name, stages:[{id,name}] }]
    const [stages, setStages] = useState([]);       // SF-only (selectors=['stage'])
    const [pipelineId, setPipelineId] = useState('');
    const [stageId, setStageId] = useState('');
    const [pipelineOpen, setPipelineOpen] = useState(false);
    const [stageOpen, setStageOpen] = useState(false);
    // 'loading' until the selector fetch resolves so we can distinguish
    // "still fetching" from "fetched and empty" — needed for the empty-state UX.
    const [selectorsState, setSelectorsState] = useState('loading'); // 'loading' | 'ready' | 'error'

    const [submitting, setSubmitting] = useState(false);
    // { done, total } while a chunked send is in flight — a 1,118-buyer send is minutes
    // of work, and a button that just says "Creating..." for four of them reads as hung.
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null); // { type:'success'|'error', message:string }

    // GHL-only: operator-chosen time to fire the opportunity's automation
    // (sent as the clozers_send_at custom field). Wall-clock datetime-local
    // string; GHL interprets it in the sub-account timezone.
    const [sendAt, setSendAt] = useState('');
    // The connected GHL account's timezone (from getGHLOpportunities). null until
    // the selector fetch resolves, or if the backend couldn't resolve it.
    const [ghlTimezone, setGhlTimezone] = useState(null);
    // Tracks whether the operator has manually edited sendAt, so the auto-seed
    // (now in the account timezone) never clobbers a chosen value.
    const sendAtTouchedRef = useRef(false);
    // New lead/provider → forget the previous manual edit so the default reseeds.
    useEffect(() => { sendAtTouchedRef.current = false; }, [provider, context.propertyId]);
    // Seed/refresh the default to "now" in the account timezone (or the
    // operator's local tz if unresolved). Never blank, so clozers_send_at can't
    // go empty and stall the wait-until-event-start step. Skips once edited.
    useEffect(() => {
        if (sendAtTouchedRef.current) return;
        setSendAt(nowInTimezone(ghlTimezone || browserTimezone()));
    }, [provider, context.propertyId, ghlTimezone]);

    // Ref-based cancel for in-flight fetches so a provider switch or retry
    // doesn't race a stale response into state.
    const fetchTokenRef = useRef(0);
    // Fires onPipelinesEmpty at most once per fetch cycle; reset each fetch.
    const emptyFiredRef = useRef(false);

    // Reset on provider switch — when a company moves from GHL → FUB the previously
    // chosen pipeline ID is meaningless.
    useEffect(() => {
        setPipelines([]); setStages([]);
        setPipelineId(''); setStageId('');
        setResult(null);
        setSelectorsState('loading');
        setGhlTimezone(null);
    }, [provider]);

    // Fetch pipelines / stages. Extracted so Retry can re-invoke it.
    const fetchSelectors = useCallback(async () => {
        if (!config || !context.companyId) return;
        const url = process.env[config.fetchPipelinesEnv];
        if (!url) { setSelectorsState('error'); return; }

        const token = ++fetchTokenRef.current;
        emptyFiredRef.current = false;
        setSelectorsState('loading');

        try {
            // GHL endpoints require an authenticated session (companyId derived
            // server-side); FUB/SF stages endpoints are unchanged.
            const callFn = provider === 'ghl' ? cloudFunctionV2Auth : cloudFunctionV2;
            const res = await callFn(url, { companyId: context.companyId });
            if (token !== fetchTokenRef.current) return;
            if (res?.status !== 200 || !res.data) {
                setSelectorsState('error');
                return;
            }

            if (provider === 'ghl') {
                // Account timezone surfaced alongside pipelines — drives the
                // schedule picker's default + label so it reasons in the CRM's
                // clock, not the operator's. May be absent (older backend / fetch
                // failed); the picker falls back to the operator's local tz.
                const tz = res.data.timezone || res.data.data?.timezone || null;
                if (tz) setGhlTimezone(tz);
                const data = res.data.pipelines || res.data.data?.pipelines || res.data;
                setPipelines(Array.isArray(data) ? data.map((p) => ({
                    id: p.id,
                    name: p.name || 'Unnamed Pipeline',
                    stages: (Array.isArray(p.stages) ? p.stages : []).map((s) => ({
                        id: s.id,
                        name: s.name || 'Unnamed Stage',
                    })),
                })) : []);
            } else if (provider === 'fub') {
                setPipelines(Array.isArray(res.data.pipelines) ? res.data.pipelines : []);
            } else if (provider === 'salesforce') {
                setStages(Array.isArray(res.data.stages) ? res.data.stages : []);
            }
            setSelectorsState('ready');
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`Error fetching ${provider} selectors:`, err);
            if (token === fetchTokenRef.current) setSelectorsState('error');
        }
    }, [config, context.companyId, provider]);

    useEffect(() => {
        fetchSelectors();
        // Bump the token on cleanup (unmount or fetchSelectors-identity change)
        // so any still-in-flight response sees a mismatch and skips its setState.
        return () => { fetchTokenRef.current += 1; };
    }, [fetchSelectors]);

    // Signal confirmed-empty up to the parent so it can switch to "Tag in CRM".
    // Must live above the action==='tag' early-return to satisfy rules-of-hooks.
    // emptyFiredRef gates re-fires until the next fetch cycle.
    const wantsPipelineForEmpty = config?.selectors?.includes('pipeline');
    const wantsStageForEmpty = config?.selectors?.includes('stage');
    const isEmptyConfirmed = selectorsState === 'ready' && (
        (wantsPipelineForEmpty && pipelines.length === 0)
        || (!wantsPipelineForEmpty && wantsStageForEmpty && stages.length === 0)
    );
    useEffect(() => {
        if (isEmptyConfirmed && !emptyFiredRef.current) {
            emptyFiredRef.current = true;
            onPipelinesEmpty?.();
        }
    }, [isEmptyConfirmed, onPipelinesEmpty]);

    // ---- TAG mode ----
    const handleTagSubmit = useCallback(async () => {
        if (selectedBuyerIds.size === 0 || tags.length === 0) return;
        const url = process.env.REACT_APP_TAG_BUYERS_IN_CRM;
        if (!url) return;
        setSubmitting(true); setResult(null);
        const chunks = chunkBuyerIds(Array.from(selectedBuyerIds));
        setProgress(chunks.length > 1 ? { done: 0, total: selectedBuyerIds.size } : null);
        try {
            // Chunked for the same reason the send path is — see crmSendChunks. A chunk
            // that doesn't come back cleanly stops the loop, and whatever was tagged
            // before it is still reported rather than thrown away.
            let data = null;
            let lastRes = null;
            let sentCount = 0;
            let chunksSent = 0;
            for (const chunk of chunks) {
                lastRes = await cloudFunctionV2Auth(url, {
                    companyId: context.companyId,
                    buyerIds: chunk,
                    tagNames: tags,
                });
                chunksSent += 1;
                data = mergeChunkData(data, lastRes?.status === 200 ? lastRes.data : null);
                sentCount += chunk.length;
                if (chunks.length > 1) setProgress({ done: sentCount, total: selectedBuyerIds.size });
                if (!shouldContinueAfterChunk(lastRes)) break;
            }
            const res = lastRes;
            // A partial run — some chunks tagged, then the loop stopped — must report what
            // landed. Falling through would tell the operator either that nothing happened
            // while their CRM holds hundreds of new tags, or (when the stop was a clean
            // 200) that the whole selection went out when most of it never was attempted.
            if (data && (data.tagged > 0) && !isCleanFullRun({ chunksSent, chunksTotal: chunks.length, lastRes: res })) {
                const partialLabel = data.crmType === 'ghl' ? 'GoHighLevel'
                    : data.crmType === 'salesforce' ? 'Salesforce'
                    : 'Follow Up Boss';
                const partialSummary = `${partialLabel}: ${data.tagged} tagged, then stopped`;
                const partialMessage = `${partialSummary} — ${selectedBuyerIds.size - data.tagged - (data.skipped || 0) - (data.failed || 0)} buyer(s) were not attempted. `
                    + 'Check your CRM, then refresh this page before retrying so you don\'t double-tag.';
                setResult({ type: 'timeout', summary: partialSummary, message: partialMessage });
                if (onCompleteSent) await onCompleteSent(partialMessage);
                return;
            }
            if (res?.status === 200 && data) {
                const parts = [];
                if (data.tagged > 0) parts.push(`${data.tagged} tagged`);
                if (data.skipped > 0) parts.push(`${data.skipped} skipped`);
                if (data.failed > 0) parts.push(`${data.failed} failed`);
                const crmLabel = data.crmType === 'ghl' ? 'GoHighLevel'
                    : data.crmType === 'salesforce' ? 'Salesforce'
                    : 'Follow Up Boss';
                // Surface why anything was skipped/failed (e.g. bad buyer email)
                // so the user can fix it — not just a bare "1 failed".
                const issues = formatCreateIssues(data);
                const tagSummary = `${crmLabel}: ${parts.join(', ')}`;
                const tagMessage = `${tagSummary}${issues.length > 0 ? ` — ${issues.join('; ')}` : ''}`;
                setResult({
                    type: data.tagged > 0 ? 'success' : 'error',
                    // Button shows the short counts; the result line carries reasons.
                    summary: tagSummary,
                    message: tagMessage,
                });
                if (data.tagged > 0 && onCompleteSent) await onCompleteSent(tagMessage);
            } else if (isLikelyTimeout(res?.status)) {
                setResult({ type: 'timeout', summary: TIMEOUT_SUMMARY, message: TIMEOUT_MESSAGE });
            } else {
                // Backend may return `error` as a string ('no_crm_connected'),
                // an Error-like object ({ message }), or omit it entirely.
                let msg;
                if (res?.error === 'no_crm_connected') {
                    msg = 'No CRM connected. Please connect in Settings.';
                } else if (typeof res?.error === 'string') {
                    msg = res.error;
                } else if (res?.error?.message) {
                    msg = res.error.message;
                } else {
                    msg = 'Failed to tag buyers.';
                }
                setResult({ type: 'error', message: msg });
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Error tagging buyers:', err);
            setResult({ type: 'error', message: 'An error occurred.' });
        } finally {
            setSubmitting(false);
            setProgress(null);
        }
    }, [selectedBuyerIds, tags, context.companyId, onCompleteSent]);

    // ---- CREATE mode ----
    const handleCreateSubmit = useCallback(async () => {
        if (selectedBuyerIds.size === 0) return;
        // Validate required selectors before submitting
        if (config.selectors.includes('pipeline') && !pipelineId) return;
        if (config.selectors.includes('stage') && !stageId) return;

        const url = process.env[config.createEnv];
        if (!url) return;

        setSubmitting(true); setResult(null);
        const chunks = chunkBuyerIds(Array.from(selectedBuyerIds));
        setProgress(chunks.length > 1 ? { done: 0, total: selectedBuyerIds.size } : null);
        try {
            // 🚨 Chunked: createGHLOpportunity tops out near 460 buyers in one call, and
            // "Select · All matches" can now hand this every matched buyer. See
            // crmSendChunks. Each chunk carries the SAME payload but its own buyer slice.
            const basePayload = config.buildCreatePayload({
                context: { ...context, selectedBuyerIds, sendAt },
                selection: { pipelineId, stageId },
                tags,
            });
            // Every create endpoint (GHL, FUB, Salesforce) requires an authenticated session.
            const callFn = cloudFunctionV2Auth;
            let data = null;
            let res = null;
            let sentCount = 0;
            let chunksSent = 0;
            for (const chunk of chunks) {
                res = await callFn(url, { ...basePayload, buyerIds: chunk });
                chunksSent += 1;
                data = mergeChunkData(data, res?.status === 200 ? res.data : null);
                sentCount += chunk.length;
                if (chunks.length > 1) setProgress({ done: sentCount, total: selectedBuyerIds.size });
                if (!shouldContinueAfterChunk(res)) break;
            }
            // A run that created records and THEN stopped is not a failure to report as
            // "nothing happened" — the opportunities exist, and the operator has to know
            // how many so a retry doesn't duplicate them. Nor is it a success: a stop on
            // a clean 200 (the pipeline vanished mid-send) would otherwise read as
            // "300 created" on a 1,118-buyer send.
            if (data && data.created > 0 && !isCleanFullRun({ chunksSent, chunksTotal: chunks.length, lastRes: res })) {
                const attempted = data.created + (data.skipped || 0) + (data.failed || 0);
                const partialSummary = `${data.created} created, then stopped`;
                const partialMessage = `${formatCreateResult(data)} — ${selectedBuyerIds.size - attempted} buyer(s) were not attempted. `
                    + 'Check your CRM, then refresh this page before retrying so you don\'t create duplicates.';
                setResult({ type: 'timeout', summary: partialSummary, message: partialMessage });
                if (onCompleteSent) await onCompleteSent(partialMessage);
                return;
            }
            if (res?.status === 200 && data) {
                const createMessage = formatCreateResult(data);
                setResult({
                    type: data.created > 0 ? 'success' : 'error',
                    // Button shows the short counts; the result line + success card
                    // carry the full message with per-buyer failure reasons.
                    summary: formatCreateSummary(data),
                    message: createMessage,
                });
                if (data.created > 0 && onCompleteSent) await onCompleteSent(createMessage);
            } else if (isLikelyTimeout(res?.status)) {
                setResult({ type: 'timeout', summary: TIMEOUT_SUMMARY, message: TIMEOUT_MESSAGE });
            } else {
                setResult({
                    type: 'error',
                    message: typeof res?.error === 'string'
                        ? res.error
                        : (res?.error?.message || `Failed to create ${config.actionVerb.toLowerCase()}`),
                });
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`Error creating ${provider} ${config.actionVerb}:`, err);
            setResult({ type: 'error', message: 'An error occurred' });
        } finally {
            setSubmitting(false);
            setProgress(null);
        }
    }, [config, context, pipelineId, stageId, selectedBuyerIds, tags, sendAt, onCompleteSent, provider]);

    // Pipeline selector — GHL/FUB use a self-contained pipelines-with-stages array,
    // so stages for the chosen pipeline come from the cascading lookup.
    const activeStages = useMemo(() => {
        if (config.selectors.includes('pipeline')) {
            return (pipelines.find((p) => p.id === pipelineId)?.stages) || [];
        }
        return stages;
    }, [config.selectors, pipelines, pipelineId, stages]);

    const buyerCount = selectedBuyerIds.size;
    const pluralBuyer = buyerCount === 1 ? 'Buyer' : 'Buyers';

    const progressSuffix = progress ? ` ${progress.done}/${progress.total}` : '';

    const renderCreateButtonText = () => {
        if (submitting) return `Creating...${progressSuffix}`;
        if (result) return result.summary || result.message;
        if (buyerCount > 0) return `Create ${config.actionVerb} for ${buyerCount} ${pluralBuyer}`;
        return 'Select Buyers';
    };

    const renderTagButtonText = () => {
        if (submitting) return `Tagging...${progressSuffix}`;
        if (result) return result.summary || result.message;
        if (buyerCount > 0) return `Tag ${buyerCount} ${pluralBuyer}`;
        return 'Select Buyers to Tag';
    };

    const createDisabled = submitting
        || buyerCount === 0
        || (config.selectors.includes('pipeline') && !pipelineId)
        || (config.selectors.includes('stage') && !stageId);

    const tagDisabled = submitting || buyerCount === 0 || tags.length === 0;

    if (action === 'tag') {
        return (
            <div className="lbm-crm-action-body">
                <TagChipInput
                    label="Tags"
                    value={tags}
                    onChange={onTagsChange}
                    placeholder="Add tags (Enter to commit)"
                    disabled={submitting}
                />
                <Button
                    buttonType="primary-button"
                    text={renderTagButtonText()}
                    cta={handleTagSubmit}
                    disabled={tagDisabled}
                    leftIcon={null}
                    rightIcon={null}
                    alt=""
                    size="large"
                    standardIcon={false}
                    badge={false}
                    badgeVal={null}
                    badgeBG={null}
                    buttonRef={null}
                    spinner={submitting}
                    disabledAction={null}
                />
                {result && (
                    <span className={`body-regular ${result.type === 'success' ? 'colour-green' : result.type === 'timeout' ? 'colour-orange' : 'colour-error'}`}>
                        {result.message}
                    </span>
                )}
            </div>
        );
    }

    // action === 'opportunity'
    const isLoadingSelectors = selectorsState === 'loading';
    const wantsPipeline = config.selectors.includes('pipeline');
    const wantsStage = config.selectors.includes('stage');
    // "Empty" once the fetch has resolved but returned no usable selectors.
    // GHL/FUB need pipelines; Salesforce only needs stages. An errored fetch
    // is NOT treated as empty — we can't claim certainty from a failed call.
    const isEmpty = selectorsState === 'ready' && (
        (wantsPipeline && pipelines.length === 0)
        || (!wantsPipeline && wantsStage && stages.length === 0)
    );
    const isErrored = selectorsState === 'error';

    const pipelineLabel = wantsPipeline
        ? (isLoadingSelectors
            ? 'Loading pipelines…'
            : isErrored
                ? `Couldn't reach ${config.crmLabel}`
                : (pipelines.find((p) => p.id === pipelineId)?.name || 'Select pipeline…'))
        : null;
    const stageLabel = wantsStage
        ? (isLoadingSelectors
            ? 'Loading…'
            : (isErrored && !wantsPipeline)
                // Salesforce: stage is the primary selector, surface error here.
                // GHL/FUB: pipeline label already carries the error + Retry, so the
                // stage label falls back to its normal "pick a pipeline first" copy.
                ? `Couldn't reach ${config.crmLabel}`
                : (activeStages.find((s) => s.id === stageId)?.name
                    || (wantsPipeline && !pipelineId ? 'Select pipeline first' : 'Select stage…')))
        : null;
    const stageDisabled = isLoadingSelectors || isErrored || (wantsPipeline && !pipelineId);
    const pipelineDisabled = isLoadingSelectors || isErrored;
    const noun = wantsPipeline ? 'pipelines' : 'stages';

    if (isEmpty) {
        // Confirmed empty (fetch resolved, zero selectors). Suppress the form;
        // the parent will typically have flipped to "Tag in CRM" already, but
        // the user can still click back to this tab and we render this panel.
        return (
            <div className="lbm-crm-action-body">
                <div className="lbm-crm-empty">
                    <Warning fill={colour.orange03} />
                    <div className="lbm-crm-empty-text">
                        <span className="body-bold colour-primary">
                            {`No ${noun} found in ${config.crmLabel}`}
                        </span>
                        <span className="body-regular colour-secondary">
                            {`Create a ${noun.slice(0, -1)} in your CRM to enable ${config.actionVerb.toLowerCase()}s, or use Tag in CRM instead.`}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="lbm-crm-action-body">
            <div className="lbm-crm-selects">
                {wantsPipeline && (
                    <div className="lbm-crm-select">
                        <span className="label-bold-caps colour-tertiary lbm-crm-select-label">
                            Pipeline
                            {isErrored && (
                                <button
                                    type="button"
                                    className="lbm-crm-retry-link label-bold cursor-pointer"
                                    onClick={fetchSelectors}
                                >
                                    Retry
                                </button>
                            )}
                        </span>
                        <div className="relative-container lbm-crm-select-control">
                            <Dropdown
                                open={pipelineOpen && !pipelineDisabled}
                                icon={null}
                                label={pipelineLabel}
                                disabled={pipelineDisabled}
                                options={pipelines.map((p) => ({
                                    label: p.name,
                                    value: p.id,
                                    selected: p.id === pipelineId,
                                }))}
                                multiselect={false}
                                cta={(option) => {
                                    setPipelineId(option.value);
                                    setStageId('');
                                    setResult(null);
                                    setPipelineOpen(false);
                                }}
                                sidealign="left"
                                topalign="bottom"
                                toggleDropdown={() => !pipelineDisabled && setPipelineOpen(!pipelineOpen)}
                                indexSelect={false}
                                selectedIndex={null}
                            />
                            {pipelineOpen && (
                                <ExitScreen clickScreen={() => setPipelineOpen(false)} />
                            )}
                        </div>
                    </div>
                )}
                {wantsStage && (
                    <div className="lbm-crm-select">
                        <span className="label-bold-caps colour-tertiary lbm-crm-select-label">
                            Stage
                            {isErrored && !wantsPipeline && (
                                <button
                                    type="button"
                                    className="lbm-crm-retry-link label-bold cursor-pointer"
                                    onClick={fetchSelectors}
                                >
                                    Retry
                                </button>
                            )}
                        </span>
                        <div className="relative-container lbm-crm-select-control">
                            <Dropdown
                                open={stageOpen && !stageDisabled}
                                icon={null}
                                label={stageLabel}
                                disabled={stageDisabled}
                                options={activeStages.map((s) => ({
                                    label: s.name,
                                    value: s.id,
                                    selected: s.id === stageId,
                                }))}
                                multiselect={false}
                                cta={(option) => {
                                    setStageId(option.value);
                                    setResult(null);
                                    setStageOpen(false);
                                }}
                                sidealign="left"
                                topalign="bottom"
                                toggleDropdown={() => !stageDisabled && setStageOpen(!stageOpen)}
                                indexSelect={false}
                                selectedIndex={null}
                            />
                            {stageOpen && (
                                <ExitScreen clickScreen={() => setStageOpen(false)} />
                            )}
                        </div>
                    </div>
                )}
            </div>

            {provider === 'ghl' && (() => {
                const effectiveTz = ghlTimezone || browserTimezone();
                const tzAbbr = getTimezoneAbbr(effectiveTz, new Date());
                // Show the IANA name too (e.g. "America/Chicago") so it's unambiguous.
                const tzDisplay = tzAbbr ? `${effectiveTz} (${tzAbbr})` : effectiveTz;
                return (
                    <div className="lbm-crm-sendat">
                        <span className="label-bold-caps colour-tertiary lbm-crm-select-label">
                            Schedule automation — {tzDisplay}
                        </span>
                        <div className="lbm-crm-sendat-control">
                            <input
                                type="datetime-local"
                                className="lbm-datetime-input body-regular colour-primary"
                                aria-label={`Schedule automation send time (${tzDisplay})`}
                                value={sendAt}
                                onChange={(e) => { sendAtTouchedRef.current = true; setSendAt(e.target.value); setResult(null); }}
                                disabled={submitting}
                            />
                            {tzAbbr && <span className="label-regular colour-secondary lbm-datetime-tz">{tzAbbr}</span>}
                        </div>
                        <span className="label-regular colour-tertiary">
                            {ghlTimezone
                                ? `Time is in your CRM account's timezone (${tzDisplay}), not your local time. Defaults to now; clearing it may stall the automation.`
                                : `Time is in ${tzDisplay}. Defaults to now; clearing it may stall the automation.`}
                        </span>
                    </div>
                );
            })()}

            <TagChipInput
                label="Add tags to buyer contact (optional)"
                value={tags}
                onChange={onTagsChange}
                placeholder="Add tags (Enter to commit)"
                disabled={submitting}
            />

            {context.imageStatus !== 'supported' && (
                <span className="triage-red-flag-inline">
                    <Warning fill={colour.orange03} />
                    {context.imageStatus === 'none' ? 'No property images' : 'Images not downloadable'}
                </span>
            )}

            <Button
                buttonType="primary-button"
                text={renderCreateButtonText()}
                cta={handleCreateSubmit}
                disabled={createDisabled}
                leftIcon={null}
                rightIcon={null}
                alt=""
                size="large"
                standardIcon={false}
                badge={false}
                badgeVal={null}
                badgeBG={null}
                buttonRef={null}
                spinner={submitting}
                disabledAction={null}
            />
            {result && (
                <span className={`body-regular ${result.type === 'success' ? 'colour-green' : result.type === 'timeout' ? 'colour-orange' : 'colour-error'}`}>
                    {result.message}
                </span>
            )}
        </div>
    );
}

export default CrmActionForm;
